
import { useState } from 'react';
import type { Customer } from '../../../../modules/customer/types';

interface CustomerPasswordModalProps {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (newPassword: string, confirmPassword: string) => Promise<void>;
}

export default function CustomerPasswordModal({
  customer,
  open,
  onClose,
  onConfirm,
}: CustomerPasswordModalProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open || !customer) return null;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(newPassword, confirmPassword);
      // 成功後重置表單
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      // 錯誤處理由父元件負責
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-[fadeIn_0.3s_ease-out]">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-password-line text-3xl text-orange-600"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            變更客戶密碼
          </h3>
          <p className="text-sm text-gray-600">
            客戶：<span className="font-medium text-gray-900">{customer.name}</span>
          </p>
          <p className="text-sm text-gray-500">
            {customer.email}
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              新密碼
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="請輸入新密碼（至少 6 個字元）"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              確認密碼
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="請再次輸入新密碼"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start">
              <i className="ri-information-line text-amber-600 mt-0.5 mr-2"></i>
              <p className="text-xs text-amber-800">
                變更密碼後，請將新密碼提供給客戶。客戶需使用新密碼登入。
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !newPassword || !confirmPassword}
            className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                處理中...
              </>
            ) : (
              <>
                <i className="ri-check-line mr-2"></i>
                確認變更
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
